import { useRouter } from 'next/router';

import {
  Checkbox,
  FieldSet,
  FormField,
  FormHelp,
  Label,
  LabeledControl,
  Legend,
  Radio,
  Select,
} from '@zillow/constellation-10';

import { AgentLicenseType as AgentLicenseTypeEnum, ForRentByType } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserPropertyEditV2WebApiHandler_agentLicenseType,
  useRentalManagerUserPropertyEditV2WebApiHandler_forRentBy,
  useRentalManagerUserPropertyEditV2WebApiHandler_hidePhone,
  useRentalManagerUserPropertyEditV2WebApiHandler_isBrokerExclusive,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import propertyUtils from '../../../utils/propertyUtils';

import { useAuth } from '../../AuthProvider';

const BROKER_LICENSE_MAP = {
  [AgentLicenseTypeEnum.REAL_ESTATE_SALES_PERSON]: 'Real Estate Sales Person',
  [AgentLicenseTypeEnum.REAL_ESTATE_BROKER]: 'Real Estate Broker',
  [AgentLicenseTypeEnum.ASSOCIATE_REAL_ESTATE_BROKER]: 'Associate Real Estate Broker',
} as const;

const BROKER_LICENSE_RESPONSE_TO_ENUM_MAP = {
  RealEstateSalesPerson: AgentLicenseTypeEnum.REAL_ESTATE_SALES_PERSON,
  RealEstateBroker: AgentLicenseTypeEnum.REAL_ESTATE_BROKER,
  AssociateRealEstateBroker: AgentLicenseTypeEnum.ASSOCIATE_REAL_ESTATE_BROKER,
} as Record<string, AgentLicenseTypeEnum>;

const forRentByFieldName = 'forRentBy';

const ForRentByFieldSet = (): JSX.Element => {
  const router = useRouter();
  const { user } = useAuth();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const isArchived = listingDetails?.isArchived || false;
  const isFeed = listingDetails?.isFeed || false;
  const isMultifamily = listingDetails?.isMultifamily || false;
  const isUpdateAgentRestricted = listingDetails?.restrictions?.isUpdateAgentRestricted || false;
  const isUpdateListingRestricted = listingDetails?.restrictions?.isUpdateListingRestricted || false;
  const isSelfServeOnboardingInProgress =
    listingDetails?.selfServeOnboardingStatus?.isSelfServeOnboardingInProgress || false;
  const isSSOBUpdateRestricted = listingDetails?.selfServeOnboardingStatus?.isUpdateRestricted || false;

  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const isRoomForRent = propertyUtils.isRoomForRent({ listing: listingDetails });

  const isInputFormDisabled =
    isFeed ||
    !canUpdateListing ||
    isArchived ||
    isUpdateListingRestricted ||
    (isSelfServeOnboardingInProgress && isSSOBUpdateRestricted);

  const updateForRentBy = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_forRentBy,
    successToastMessage: 'Listed by updated',
    meta: {
      context: { api: 'RentalManagerUserPropertyEditV2WebApiHandler_forRentBy', details: { listingAlias } },
    },
  });

  const updateIsBrokerExclusive = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_isBrokerExclusive,
    successToastMessage: 'Broker information updated',
    meta: {
      context: { api: 'RentalManagerUserPropertyEditV2WebApiHandler_isBrokerExclusive', details: { listingAlias } },
    },
  });

  const { mutateAsync: updateHidePhone } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_hidePhone,
    skipToast: true,
    meta: {
      context: { api: 'RentalManagerUserPropertyEditV2WebApiHandler_hidePhone', details: { listingAlias } },
    },
  });

  const updateAgentLicenseType = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_agentLicenseType,
    successToastMessage: 'License type updated',
    meta: {
      context: { api: 'RentalManagerUserPropertyEditV2WebApiHandler_agentLicenseType', details: { listingAlias } },
    },
  });

  const forRentByField = useFieldSave({
    fieldName: forRentByFieldName,
    initialValue: listingDetails?.forRentByType || '',
    mutation: updateForRentBy,
    buildMutationVariables: (value) => ({
      postData: { forRentBy: value as ForRentByType, listingAlias },
    }),
    autoSaveOnChange: true,
  });

  const brokerExclusiveField = useFieldSave({
    fieldName: 'isListingExclusive',
    initialValue: listingDetails?.agent?.isExclusive || false,
    mutation: updateIsBrokerExclusive,
    buildMutationVariables: (value) => ({
      postData: { isBrokerExclusive: value as boolean, listingAlias },
    }),
    autoSaveOnChange: true,
    enabled: forRentByField.value === ForRentByType.AGENT && !isUpdateAgentRestricted,
  });

  const agentLicenseTypeField = useFieldSave({
    fieldName: 'agentLicenseType',
    initialValue: listingDetails?.agent?.licenseType
      ? BROKER_LICENSE_RESPONSE_TO_ENUM_MAP[listingDetails?.agent?.licenseType]
      : '',
    mutation: updateAgentLicenseType,
    buildMutationVariables: (value) => ({
      postData: {
        agentLicenseType: value === '' ? undefined : (value as AgentLicenseTypeEnum),
        listingAlias,
      },
    }),
    autoSaveOnChange: true,
    enabled: forRentByField.value === ForRentByType.AGENT && !isUpdateAgentRestricted,
  });

  const handleForRentByChange = (value: string) => {
    forRentByField.onChange(value);
    if (value === ForRentByType.AGENT) {
      updateHidePhone({ postData: { hidePhone: false, listingAlias } });
    }
  };

  return (
    <div data-testid="ForRentByFieldSet">
      {!isRoomForRent && (
        <FieldSet
          disabled={isInputFormDisabled}
          legend={
            <Legend>
              <Label required={!isInputFormDisabled}>Listed by</Label>
            </Legend>
          }
          error={Boolean(forRentByField.error)}
          description={<FormHelp>{forRentByField.error}</FormHelp>}
        >
          <LabeledControl
            controlId="propertyOwner"
            label={<Label>Property owner</Label>}
            control={
              <Radio
                name={forRentByFieldName}
                value={ForRentByType.OWNER}
                checked={forRentByField.value === ForRentByType.OWNER}
                onChange={(e) => handleForRentByChange(e.target.value)}
              />
            }
          />
          <LabeledControl
            controlId="managementCompanyOrBroker"
            label={<Label>Management company or broker</Label>}
            control={
              <Radio
                name={forRentByFieldName}
                value={ForRentByType.AGENT}
                checked={forRentByField.value === ForRentByType.AGENT}
                onChange={(e) => handleForRentByChange(e.target.value)}
              />
            }
          />

          {!isMultifamily && (
            <LabeledControl
              controlId="tenant"
              label={<Label>Tenant</Label>}
              control={
                <Radio
                  name={forRentByFieldName}
                  value={ForRentByType.TENANT}
                  checked={forRentByField.value === ForRentByType.TENANT}
                  onChange={(e) => handleForRentByChange(e.target.value)}
                />
              }
            />
          )}
        </FieldSet>
      )}

      {forRentByField.value === ForRentByType.AGENT && !isUpdateAgentRestricted && (
        <>
          <FormField
            label={<Label>Additional broker information</Label>}
            error={Boolean(brokerExclusiveField.error)}
            description={<FormHelp>{brokerExclusiveField.error}</FormHelp>}
          >
            <LabeledControl
              controlId="broker-exclusive"
              label={<Label>Listing is exclusive</Label>}
              control={
                <Checkbox
                  checked={brokerExclusiveField.value}
                  onChange={(e) => brokerExclusiveField.onChange(e.target.checked)}
                />
              }
              css={{ marginBlock: '200' }}
            />
          </FormField>

          <FormField
            label={<Label>License type</Label>}
            error={Boolean(agentLicenseTypeField.error)}
            description={<FormHelp>{agentLicenseTypeField.error}</FormHelp>}
          >
            <Select
              value={agentLicenseTypeField.value}
              onChange={(e) => agentLicenseTypeField.onChange(e.target.value)}
              size="md"
              css={{ marginBlock: '200', whiteSpace: 'pre' }}
            >
              <option value="" key="no-value" aria-label="no-value" />
              {Object.keys(BROKER_LICENSE_MAP).map((key) => (
                <option key={key} value={key} aria-label={BROKER_LICENSE_MAP[key as keyof typeof BROKER_LICENSE_MAP]}>
                  {BROKER_LICENSE_MAP[key as keyof typeof BROKER_LICENSE_MAP]}
                </option>
              ))}
            </Select>
          </FormField>
        </>
      )}
    </div>
  );
};

export default ForRentByFieldSet;
